// HFDatasetPicker.jsx
// ─────────────────────────────────────────────────────────────────────────────
// Shared HuggingFace Dataset Search & Loader
// Used by: AutoEDA, Document Analysis, ML Hub, DL Hub
//
// Flow: Search → Select dataset → Pick split → Preview → Acknowledge license → Load
// Data source: HuggingFace Hub API (public, CORS-enabled, no key required)
// Output: calls onLoaded(csvText, fileName, sizeBytes) when user loads data
// textMode: outputs plain text (concatenated text column) instead of CSV
// ─────────────────────────────────────────────────────────────────────────────
import { useState, useRef } from 'react';
import { C } from './labs/wsShared';

const FONT = "'JetBrains Mono', monospace";
const HF_ORANGE = '#ff9d00';
const HF_API   = 'https://huggingface.co/api';
const DS_SRV   = 'https://datasets-server.huggingface.co';

// Licenses that carry a commercial-use restriction or copyleft concern
const LICENSE_WARN = {
  'cc-by-nc-4.0':    'Non-commercial use only.',
  'cc-by-nc-sa-4.0': 'Non-commercial use only.',
  'cc-by-nc-nd-4.0': 'Non-commercial use only.',
  'gpl-3.0':         'Copyleft — check your use case.',
  'agpl-3.0':        'Copyleft — check your use case.',
  'unknown':         'License unclear — verify on HuggingFace before use.',
};

const LICENSE_COLOR = (l = '') => {
  if (!l || l === 'unknown') return C.t4;
  if (l.startsWith('cc-by-nc') || l.startsWith('gpl') || l.startsWith('agpl')) return C.gold;
  if (l.startsWith('cc-by-sa')) return '#fbbf24';
  return C.green;
};

const fmtNum = (n) => {
  if (!n) return '0';
  if (n >= 1e6) return (n / 1e6).toFixed(1) + 'M';
  if (n >= 1e3) return (n / 1e3).toFixed(1) + 'K';
  return String(n);
};

const fmtBytes = (b) => {
  if (!b) return '?';
  if (b >= 1e9) return (b / 1e9).toFixed(1) + ' GB';
  if (b >= 1e6) return (b / 1e6).toFixed(1) + ' MB';
  if (b >= 1e3) return (b / 1e3).toFixed(0) + ' KB';
  return b + ' B';
};

// Extract size_categories tag → human-readable row-range label
// HF tag format: "size_categories:100K<n<1M"
const SIZE_LABELS = {
  'n<1K':           { label: '< 1K rows',      color: C.t4  },
  '1K<n<10K':       { label: '1K – 10K rows',  color: C.t3  },
  '10K<n<100K':     { label: '10K – 100K rows',color: C.t2  },
  '100K<n<1M':      { label: '100K – 1M rows', color: C.cyan },
  '1M<n<10M':       { label: '1M – 10M rows',  color: C.green },
  '10M<n<100M':     { label: '10M – 100M rows',color: C.gold },
  '100M<n<1B':      { label: '100M – 1B rows', color: C.orange },
  'n>1B':           { label: '> 1B rows',      color: C.red  },
};

const getSizeInfo = (ds) => {
  const tag = (ds?.tags || []).find(t => typeof t === 'string' && t.startsWith('size_categories:'));
  if (!tag) return null;
  const key = tag.slice('size_categories:'.length);
  return SIZE_LABELS[key] || { label: key, color: C.t4 };
};

// Extract first task_categories or task_ids tag → short label
const getTaskLabel = (ds) => {
  const tag = (ds?.tags || []).find(t =>
    typeof t === 'string' && (t.startsWith('task_categories:') || t.startsWith('task_ids:'))
  );
  if (!tag) return null;
  return tag.includes(':') ? tag.split(':')[1].replace(/-/g, ' ') : tag;
};

// Dtype display helper
// HF first-rows features shape: { name, feature_idx, type: { dtype, _type } }
// fallback shapes: { name, dtype } or { name, type: "string" }
const dtypeStr = (f) => {
  if (!f) return 'unknown';
  // most common: f.type.dtype
  if (f.type && typeof f.type === 'object' && f.type.dtype) return f.type.dtype;
  // f.type may be a string directly (older API responses)
  if (f.type && typeof f.type === 'string') return f.type;
  // legacy flat shape
  const d = f.dtype;
  if (!d) return 'unknown';
  if (typeof d === 'string') return d;
  return d.dtype || d.class || 'object';
};

const isNumericDtype = (f) => {
  const d = dtypeStr(f);
  return ['int8','int16','int32','int64','uint8','uint16','uint32','uint64',
          'float16','float32','float64','double','float','int'].includes(d);
};

const isStringDtype = (f) => {
  const t = f?.type;
  if (t && typeof t === 'object' && t._type === 'Sequence') return false;
  return dtypeStr(f) === 'string';
};

// ── Sanitise a single cell value for CSV ─────────────────────────────────────
// parseCSV in wsShared splits on \n BEFORE handling quotes, so we must
// remove all newlines from cell values to avoid row misalignment.
function escapeCsvCell(v) {
  if (v === null || v === undefined) return '';
  let s;
  if (Array.isArray(v)) {
    // Flatten 1-level arrays to semicolon-separated (never use commas here)
    s = v.map(x => (x === null || x === undefined) ? '' : String(x).replace(/[\n\r]+/g, ' ')).join('; ');
  } else if (typeof v === 'object') {
    s = JSON.stringify(v);
  } else {
    s = String(v);
  }
  // Strip ALL newlines/carriage returns — parseCSV can't handle multi-line cells
  s = s.replace(/[\r\n]+/g, ' ').trim();
  // Truncate very long strings so CSV stays manageable
  if (s.length > 512) s = s.slice(0, 512) + '…';
  // Wrap in quotes only if the value contains a comma or a double-quote
  if (s.includes(',') || s.includes('"')) {
    s = '"' + s.replace(/"/g, '""') + '"';
  }
  return s;
}

// ── CSV conversion ────────────────────────────────────────────────────────────
function rowsToCsv(features, rows) {
  // Derive column list from the first actual row's keys — more reliable than
  // the features array whose structure can vary across HF API versions.
  const firstObj = rows[0] ? (rows[0].row ?? rows[0]) : null;
  const cols = firstObj
    ? Object.keys(firstObj)
    : features.map(f => f.name);

  const header = cols.map(c => escapeCsvCell(c)).join(',');
  const body   = rows.map(r => {
    const obj = r.row ?? r;
    return cols.map(c => escapeCsvCell(obj[c])).join(',');
  });
  return [header, ...body].join('\n');
}

// ── Plain-text concatenation (textMode) ───────────────────────────────────────
function rowsToText(rows, col) {
  return rows
    .map((r, i) => {
      const obj = r.row ?? r;
      const v = obj[col] ?? '';
      return `--- Document ${i + 1} ---\n${typeof v === 'string' ? v : JSON.stringify(v)}`;
    })
    .join('\n\n');
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
export default function HFDatasetPicker({ onLoaded, onClose, accentColor, textMode = false }) {
  const accent = accentColor || HF_ORANGE;

  // Phase: 'search' | 'detail' | 'loading'
  const [phase,    setPhase]    = useState('search');

  // Search
  const [query,      setQuery]      = useState('');
  const [results,    setResults]    = useState([]);
  const [searching,  setSearching]  = useState(false);
  const [searchErr,  setSearchErr]  = useState('');

  // Detail
  const [selected,      setSelected]      = useState(null);
  const [splits,        setSplits]        = useState([]);
  const [activeSplit,   setActiveSplit]   = useState(null);
  const [preview,       setPreview]       = useState(null);   // { features, rows }
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailErr,     setDetailErr]     = useState('');
  const [textCol,       setTextCol]       = useState('');
  const [acknowledged,  setAcknowledged]  = useState(false);

  // Loading
  const [loadProgress, setLoadProgress] = useState(0);
  const [loadStage,    setLoadStage]    = useState('');
  const [loadErr,      setLoadErr]      = useState('');

  // Per-split row/size data from HF /size API (background-loaded, non-critical)
  const [splitSizes, setSplitSizes] = useState({}); // "config/split" → { num_rows, num_bytes }

  const searchTimer = useRef(null);

  // ── Search ──────────────────────────────────────────────────────────────────
  const doSearch = async (q) => {
    if (!q.trim()) { setResults([]); return; }
    setSearching(true); setSearchErr('');
    try {
      const res = await fetch(
        `${HF_API}/datasets?search=${encodeURIComponent(q)}&limit=20&sort=downloads&direction=-1`
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setResults(Array.isArray(data) ? data : []);
    } catch (e) {
      setSearchErr(
        e.message.includes('Failed to fetch') || e.message.includes('NetworkError')
          ? 'Network error. Check your connection or try disabling browser extensions.'
          : e.message
      );
    } finally {
      setSearching(false);
    }
  };

  const handleQueryChange = (v) => {
    setQuery(v);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => doSearch(v), 450);
  };

  // ── Select dataset → fetch splits + preview ─────────────────────────────────
  const selectDataset = async (ds) => {
    setSelected(ds);
    setPhase('detail');
    setDetailLoading(true);
    setDetailErr('');
    setPreview(null);
    setSplits([]);
    setActiveSplit(null);
    setAcknowledged(false);
    setTextCol('');
    setSplitSizes({});

    // Fire non-critical /size fetch in background to get actual row counts
    fetch(`${DS_SRV}/size?dataset=${encodeURIComponent(ds.id)}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data) return;
        const map = {};
        (data.size?.splits || []).forEach(s => {
          map[`${s.config}/${s.split}`] = { num_rows: s.num_rows, num_bytes: s.num_bytes_parquet_files };
        });
        setSplitSizes(map);
      })
      .catch(() => {}); // non-critical — don't block detail load on failure

    try {
      const splitRes = await fetch(
        `${DS_SRV}/splits?dataset=${encodeURIComponent(ds.id)}`
      );
      if (!splitRes.ok) {
        const body = await splitRes.json().catch(() => ({}));
        throw new Error(body?.error || `Viewer API unavailable for this dataset (HTTP ${splitRes.status}).`);
      }
      const splitData = await splitRes.json();
      const allSplits = splitData.splits || [];
      if (allSplits.length === 0) throw new Error('No splits found.');
      setSplits(allSplits);

      const first = allSplits.find(s => s.split === 'train') || allSplits[0];
      setActiveSplit(first);
      await fetchPreview(ds.id, first.config, first.split);
    } catch (e) {
      setDetailErr(e.message);
    } finally {
      setDetailLoading(false);
    }
  };

  const fetchPreview = async (dsId, config, split) => {
    setDetailLoading(true);
    setDetailErr('');
    try {
      const res = await fetch(
        `${DS_SRV}/first-rows?dataset=${encodeURIComponent(dsId)}&config=${encodeURIComponent(config)}&split=${encodeURIComponent(split)}`
      );
      if (!res.ok) throw new Error(`Preview unavailable (HTTP ${res.status}).`);
      const data = await res.json();
      setPreview(data);

      // Auto-select the best text column
      const textFeature = (data.features || []).find(f =>
        ['text', 'content', 'abstract', 'sentence', 'document', 'body', 'review',
         'title', 'summary', 'description', 'passage', 'context'].includes(f.name?.toLowerCase())
        || isStringDtype(f)
      );
      if (textFeature) setTextCol(textFeature.name);
    } catch (e) {
      setDetailErr(e.message);
    } finally {
      setDetailLoading(false);
    }
  };

  const changeSplit = async (splitItem) => {
    if (!selected) return;
    setActiveSplit(splitItem);
    setPreview(null);
    await fetchPreview(selected.id, splitItem.config, splitItem.split);
  };

  // ── Load rows ────────────────────────────────────────────────────────────────
  const loadRows = async (maxRows) => {
    if (!selected || !activeSplit || !preview) return;
    const { config, split } = activeSplit;
    const dsId     = selected.id;
    const features = preview.features || [];
    const BATCH    = 100;

    // Use actual row count from /size API for accurate progress + capping
    const knownTotal = splitSizes[`${config}/${split}`]?.num_rows;
    const effectiveCap = knownTotal ? Math.min(maxRows, knownTotal) : maxRows;
    const batches  = Math.ceil(effectiveCap / BATCH);

    setPhase('loading');
    setLoadProgress(0);
    setLoadErr('');

    let allRows = [];
    try {
      for (let i = 0; i < batches; i++) {
        const from = i * BATCH;
        const to   = Math.min(from + BATCH, effectiveCap);
        setLoadStage(`Fetching rows ${from + 1}–${to}…`);

        const res = await fetch(
          `${DS_SRV}/rows?dataset=${encodeURIComponent(dsId)}&config=${encodeURIComponent(config)}&split=${encodeURIComponent(split)}&offset=${from}&length=${BATCH}`
        );

        if (!res.ok) {
          if (res.status === 416) break; // offset exceeds dataset size
          throw new Error(`Fetch failed at batch ${i + 1} (HTTP ${res.status}).`);
        }

        const data  = await res.json();
        const batch = data.rows || [];
        allRows = allRows.concat(batch);
        setLoadProgress(Math.round((allRows.length / effectiveCap) * 90));
        if (batch.length < BATCH) break; // reached end of dataset
      }

      if (allRows.length === 0) throw new Error('No rows returned. Dataset may be empty or unsupported.');

      setLoadStage('Converting to CSV…');
      const isText = textMode && textCol;
      const output = isText ? rowsToText(allRows, textCol) : rowsToCsv(features, allRows);
      const ext    = isText ? 'txt'   : 'csv';
      const name   = `${dsId.replace('/', '_')}_${split}_${allRows.length}rows.${ext}`;
      const size   = output.length;

      setLoadProgress(100);
      await new Promise(r => setTimeout(r, 150)); // let user see 100%
      onLoaded(output, name, size);
      onClose?.(); // auto-close modal on successful load
    } catch (e) {
      setLoadErr(e.message);
      setPhase('detail');
    }
  };

  // ── Helpers ──────────────────────────────────────────────────────────────────
  const getLicense = (ds) => {
    // cardData.license is populated by the /datasets/:id endpoint but rarely
    // present in search results. The tags array is always returned and contains
    // entries like "license:mit", "license:apache-2.0", "license:cc-by-4.0".
    if (ds?.cardData?.license) return ds.cardData.license;
    const tag = (ds?.tags || []).find(t => typeof t === 'string' && t.startsWith('license:'));
    if (tag) return tag.slice('license:'.length);
    return 'unknown';
  };

  // ══════════════════════════════════════════════════════════════════════════════
  // RENDER — fixed modal overlay
  // ══════════════════════════════════════════════════════════════════════════════

  // Values used in modal header (detail phase) and detail body
  const lic      = getLicense(selected);
  const warn     = LICENSE_WARN[lic];
  const licC     = LICENSE_COLOR(lic);
  const features = preview?.features || [];
  const rows     = preview?.rows     || [];
  const strCols  = features.filter(f => isStringDtype(f));

  // Phase content rendered inside the modal body
  const renderContent = () => {

  // ── SEARCH PHASE ───────────────────────────────────────────────────────────
  if (phase === 'search') return (
    <div style={{ fontFamily: FONT }}>

      {/* Search input */}
      <div style={{ position: 'relative', marginBottom: 14 }}>
        <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 14, opacity: 0.4 }}>🔍</span>
        <input
          autoFocus
          value={query}
          onChange={e => handleQueryChange(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && doSearch(query)}
          placeholder="e.g. iris, titanic, imdb, squad, cifar10, amazon_reviews…"
          style={{
            width: '100%', boxSizing: 'border-box',
            background: C.deep, border: `1px solid ${C.b2}`,
            borderRadius: 6, padding: '12px 40px 12px 38px',
            fontSize: 11, color: C.t1, fontFamily: FONT, outline: 'none',
            transition: 'border-color .15s',
          }}
          onFocus={e => { e.target.style.borderColor = HF_ORANGE; }}
          onBlur={e  => { e.target.style.borderColor = C.b2; }}
        />
        {searching
          ? <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 8.5, color: C.t4, letterSpacing: '.08em' }}>SEARCHING…</span>
          : query &&
            <button
              onClick={() => doSearch(query)}
              style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', padding: '4px 10px', background: `${HF_ORANGE}18`, border: `1px solid ${HF_ORANGE}50`, color: HF_ORANGE, fontSize: 8.5, fontFamily: FONT, fontWeight: 700, borderRadius: 4, cursor: 'pointer' }}>
              SEARCH
            </button>
        }
      </div>

      {searchErr && (
        <div style={{ padding: '9px 13px', background: `${C.red}10`, border: `1px solid ${C.red}40`, borderRadius: 5, fontSize: 10, color: C.red, marginBottom: 12 }}>
          ⚠ {searchErr}
        </div>
      )}

      {/* Popular tags when no query */}
      {!query && !searching && (
        <div>
          <div style={{ fontSize: 8.5, color: C.t4, letterSpacing: '.1em', marginBottom: 8 }}>POPULAR DATASETS — click to search</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 20 }}>
            {['iris', 'titanic', 'mnist', 'imdb', 'squad', 'ag_news', 'cola', 'diabetes',
              'california_housing', 'wine_quality', 'amazon_reviews_multi'].map(name => (
              <button key={name}
                onClick={() => { setQuery(name); handleQueryChange(name); }}
                style={{
                  padding: '4px 11px', background: C.deep, border: `1px solid ${C.b1}`,
                  borderRadius: 20, fontSize: 9.5, color: C.t2, cursor: 'pointer',
                  fontFamily: FONT, transition: 'all .12s',
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = HF_ORANGE; e.currentTarget.style.color = HF_ORANGE; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = C.b1; e.currentTarget.style.color = C.t2; }}>
                {name}
              </button>
            ))}
          </div>

          {/* Info box */}
          <div style={{ padding: '12px 16px', background: `${HF_ORANGE}08`, border: `1px solid ${HF_ORANGE}25`, borderRadius: 6, fontSize: 9.5, color: C.t3, lineHeight: 1.8 }}>
            <div style={{ fontWeight: 700, color: C.t2, marginBottom: 4 }}>How it works</div>
            <div>① Search a dataset · ② Preview rows & columns · ③ Accept license · ④ Load into platform</div>
            <div style={{ marginTop: 4, color: C.t4 }}>Uses HuggingFace Dataset Viewer API — public, no API key needed.</div>
            {textMode && <div style={{ marginTop: 4, color: C.violet }}>📝 Text mode: pick a text column, all rows become a document for analysis.</div>}
          </div>
        </div>
      )}

      {/* Results list */}
      {results.length > 0 && (
        <div>
          <div style={{ fontSize: 8.5, color: C.t4, letterSpacing: '.08em', marginBottom: 8 }}>{results.length} DATASETS FOUND · sorted by downloads</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            {results.map(ds => {
              const dsLic   = getLicense(ds);
              const dsWarn  = LICENSE_WARN[dsLic];
              const dsLicC  = LICENSE_COLOR(dsLic);
              const sizeInfo = getSizeInfo(ds);
              const taskLabel = getTaskLabel(ds);
              return (
                <div key={ds.id}
                  onClick={() => selectDataset(ds)}
                  style={{
                    display: 'flex', alignItems: 'flex-start', gap: 12,
                    padding: '11px 14px', background: C.deep, border: `1px solid ${C.b1}`,
                    borderRadius: 6, cursor: 'pointer', transition: 'all .12s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = HF_ORANGE; e.currentTarget.style.background = `${HF_ORANGE}06`; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = C.b1; e.currentTarget.style.background = C.deep; }}>

                  <span style={{ fontSize: 18, opacity: 0.7, flexShrink: 0, paddingTop: 1 }}>🤗</span>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap', marginBottom: 4 }}>
                      <span style={{ fontWeight: 700, fontSize: 11, color: C.t1 }}>{ds.id}</span>
                      <span style={{
                        fontSize: 8, padding: '1px 6px', borderRadius: 10,
                        background: `${dsLicC}15`, color: dsLicC, border: `1px solid ${dsLicC}35`,
                      }}>{dsLic}</span>
                      {dsWarn && <span style={{ fontSize: 8, color: C.gold }}>⚠ {dsWarn}</span>}
                    </div>

                    {(ds.description || ds.cardData?.pretty_name) && (
                      <div style={{
                        fontSize: 9.5, color: C.t3, lineHeight: 1.55, marginBottom: 6,
                        overflow: 'hidden', display: '-webkit-box',
                        WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                      }}>
                        {ds.description || ds.cardData?.pretty_name}
                      </div>
                    )}

                    {/* Size + task row */}
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 5 }}>
                      {sizeInfo && (
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 3,
                          fontSize: 8.5, padding: '2px 7px', borderRadius: 4,
                          background: `${sizeInfo.color}14`, color: sizeInfo.color,
                          border: `1px solid ${sizeInfo.color}35`, fontWeight: 700,
                        }}>
                          📊 {sizeInfo.label}
                        </span>
                      )}
                      {taskLabel && (
                        <span style={{
                          fontSize: 8.5, padding: '2px 7px', borderRadius: 4,
                          background: `${C.violet}12`, color: C.violet,
                          border: `1px solid ${C.violet}30`,
                        }}>
                          {taskLabel}
                        </span>
                      )}
                    </div>

                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                      {ds.downloads > 0 && <span style={{ fontSize: 8.5, color: C.t4 }}>↓ {fmtNum(ds.downloads)}</span>}
                      {ds.likes    > 0 && <span style={{ fontSize: 8.5, color: C.t4 }}>♥ {fmtNum(ds.likes)}</span>}
                    </div>
                  </div>

                  <span style={{ fontSize: 9, color: HF_ORANGE, fontWeight: 700, flexShrink: 0, paddingTop: 2 }}>
                    SELECT →
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* No results */}
      {!searching && query && results.length === 0 && !searchErr && (
        <div style={{ textAlign: 'center', padding: '30px 0', color: C.t4, fontSize: 10 }}>
          No datasets found for "{query}". Try a broader term.
        </div>
      )}
    </div>
  );

  // ── DETAIL PHASE ──────────────────────────────────────────────────────────────
  if (phase === 'detail' && selected) {
    const lic  = getLicense(selected);
    const warn = LICENSE_WARN[lic];
    const licC = LICENSE_COLOR(lic);
    const features = preview?.features || [];
    const rows     = preview?.rows     || [];
    const strCols  = features.filter(f => isStringDtype(f));
    const splitKey  = activeSplit ? `${activeSplit.config}/${activeSplit.split}` : '';
    const sizeData  = splitSizes[splitKey]; // { num_rows, num_bytes } — populated after /size resolves
    const actualRows = sizeData?.num_rows ?? null;
    const tooLarge   = actualRows != null && actualRows > 500_000;

    return (
      <div style={{ fontFamily: FONT }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <button
            onClick={() => { setPhase('search'); }}
            style={{ padding: '5px 10px', background: C.surface, border: `1px solid ${C.b1}`, color: C.t2, fontSize: 9, fontFamily: FONT, borderRadius: 4, cursor: 'pointer', flexShrink: 0 }}>
            ← Back
          </button>
          <span style={{ fontSize: 20 }}>🤗</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 12, color: C.t1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {selected.id}
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 2 }}>
              <span style={{ fontSize: 8, padding: '1px 6px', borderRadius: 10, background: `${licC}15`, color: licC, border: `1px solid ${licC}35` }}>{lic}</span>
              {warn && <span style={{ fontSize: 8, color: C.gold }}>⚠ {warn}</span>}
              <a href={`https://huggingface.co/datasets/${selected.id}`} target="_blank" rel="noopener noreferrer"
                style={{ fontSize: 8.5, color: HF_ORANGE, textDecoration: 'none' }}>
                View on HF ↗
              </a>
            </div>
          </div>
        </div>

        {/* Detail loading spinner */}
        {detailLoading && (
          <div style={{ textAlign: 'center', padding: '28px 0', color: C.t4, fontSize: 10 }}>
            ⏳ Fetching dataset metadata…
          </div>
        )}

        {/* Error */}
        {detailErr && !detailLoading && (
          <div style={{ padding: '11px 14px', background: `${C.red}10`, border: `1px solid ${C.red}40`, borderRadius: 5, fontSize: 10, color: C.red, marginBottom: 12, lineHeight: 1.6 }}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>⚠ Unable to load dataset</div>
            {detailErr}
            <div style={{ marginTop: 6, color: C.t4 }}>
              This dataset may not be indexed by the HuggingFace Viewer API, may require authentication, or may be an image/audio dataset. Try searching for a different one.
            </div>
          </div>
        )}

        {!detailLoading && preview && (
          <>
            {/* Split selector */}
            {splits.length > 1 && (
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 8.5, color: C.t4, letterSpacing: '.1em', marginBottom: 6 }}>SPLIT</div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {splits.map(s => {
                    const isActive = activeSplit?.split === s.split && activeSplit?.config === s.config;
                    return (
                      <button key={`${s.config}_${s.split}`}
                        onClick={() => changeSplit(s)}
                        style={{
                          padding: '5px 13px', fontFamily: FONT, fontSize: 9, fontWeight: 700, cursor: 'pointer',
                          background: isActive ? `${HF_ORANGE}18` : C.deep,
                          border:     `1px solid ${isActive ? HF_ORANGE : C.b1}`,
                          color:      isActive ? HF_ORANGE : C.t2,
                          borderRadius: 4, transition: 'all .12s',
                        }}>
                        {s.split}{s.config && s.config !== 'default' ? ` · ${s.config}` : ''}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Stats row */}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
              {[
                { label: 'COLUMNS',  val: features.length },
                { label: 'NUMERIC',  val: features.filter(isNumericDtype).length, color: C.cyan },
                { label: 'TEXT',     val: strCols.length,                         color: C.violet },
                { label: 'PREVIEW',  val: `${rows.length} rows` },
                ...(sizeData ? [
                  { label: 'TOTAL ROWS', val: fmtNum(sizeData.num_rows), color: C.t1 },
                  { label: 'SIZE',       val: fmtBytes(sizeData.num_bytes), color: C.t2 },
                ] : []),
              ].map(({ label, val, color }) => (
                <div key={label} style={{ padding: '8px 12px', background: C.deep, border: `1px solid ${C.b1}`, borderRadius: 4, fontSize: 9 }}>
                  <div style={{ color: C.t4, marginBottom: 2, letterSpacing: '.08em' }}>{label}</div>
                  <div style={{ fontWeight: 700, color: color || C.t1 }}>{val}</div>
                </div>
              ))}
            </div>

            {/* Column type badges */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 8.5, color: C.t4, letterSpacing: '.1em', marginBottom: 7 }}>SCHEMA</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                {features.map(f => {
                  const dt  = dtypeStr(f);
                  const col = isNumericDtype(f) ? C.cyan : isStringDtype(f) ? C.violet : C.gold;
                  return (
                    <span key={f.name} style={{
                      fontSize: 9, padding: '2px 8px',
                      background: `${col}12`, color: col, border: `1px solid ${col}30`,
                      borderRadius: 3,
                    }}>
                      {f.name} <span style={{ opacity: 0.65 }}>{dt}</span>
                    </span>
                  );
                })}
              </div>
            </div>

            {/* Text column picker (textMode) */}
            {textMode && strCols.length > 0 && (
              <div style={{ marginBottom: 14, padding: '12px 14px', background: `${C.violet}08`, border: `1px solid ${C.violet}30`, borderRadius: 6 }}>
                <div style={{ fontSize: 8.5, color: C.violet, letterSpacing: '.1em', marginBottom: 7, fontWeight: 700 }}>TEXT COLUMN TO ANALYZE</div>
                <select value={textCol} onChange={e => setTextCol(e.target.value)}
                  style={{ background: C.deep, border: `1px solid ${C.b2}`, color: C.t1, padding: '8px 12px', fontSize: 10, fontFamily: FONT, borderRadius: 4, outline: 'none', width: '100%' }}>
                  {strCols.map(f => <option key={f.name} value={f.name}>{f.name}</option>)}
                </select>
                <div style={{ marginTop: 6, fontSize: 8.5, color: C.t4 }}>
                  Each row's value will become a separate document segment.
                </div>
              </div>
            )}

            {/* Preview table */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 8.5, color: C.t4, letterSpacing: '.1em', marginBottom: 7 }}>
                DATA PREVIEW · first {Math.min(rows.length, 5)} rows
              </div>
              <div style={{ overflowX: 'auto', borderRadius: 5, border: `1px solid ${C.b1}` }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 9, fontFamily: FONT }}>
                  <thead>
                    <tr style={{ background: C.deep }}>
                      {features.slice(0, 6).map(f => (
                        <th key={f.name} style={{
                          padding: '7px 10px', textAlign: 'left', color: C.t3,
                          fontWeight: 700, borderBottom: `1px solid ${C.b1}`,
                          whiteSpace: 'nowrap', letterSpacing: '.07em',
                        }}>{f.name}</th>
                      ))}
                      {features.length > 6 &&
                        <th style={{ padding: '7px 10px', color: C.t4, fontWeight: 400 }}>+{features.length - 6} more</th>
                      }
                    </tr>
                  </thead>
                  <tbody>
                    {rows.slice(0, 5).map((r, i) => {
                      const obj = r.row ?? r;
                      return (
                        <tr key={i} style={{ borderBottom: i < Math.min(rows.length, 5) - 1 ? `1px solid ${C.b0}` : 'none' }}>
                          {features.slice(0, 6).map(f => {
                            const v = obj[f.name];
                            const display = v === null || v === undefined ? <span style={{ color: C.t4, fontStyle: 'italic' }}>null</span>
                              : typeof v === 'object' ? JSON.stringify(v).substring(0, 45) + (JSON.stringify(v).length > 45 ? '…' : '')
                              : String(v).substring(0, 55) + (String(v).length > 55 ? '…' : '');
                            return (
                              <td key={f.name} style={{
                                padding: '5px 10px', color: C.t2,
                                borderRight: `1px solid ${C.b0}`,
                                maxWidth: 160, overflow: 'hidden',
                                textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                              }}>{display}</td>
                            );
                          })}
                          {features.length > 6 && <td style={{ padding: '5px 10px', color: C.t4 }}>…</td>}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* License acknowledgment */}
            <div style={{
              padding: '12px 14px', background: `${HF_ORANGE}08`,
              border: `1px solid ${HF_ORANGE}28`, borderRadius: 6, marginBottom: 14,
            }}>
              <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer' }}>
                <input type="checkbox" checked={acknowledged} onChange={e => setAcknowledged(e.target.checked)}
                  style={{ marginTop: 2, accentColor: HF_ORANGE, flexShrink: 0, width: 13, height: 13 }} />
                <span style={{ fontSize: 9.5, color: C.t2, lineHeight: 1.7 }}>
                  I acknowledge the{' '}
                  <strong style={{ color: HF_ORANGE }}>{lic}</strong>{' '}
                  license for{' '}
                  <strong style={{ color: C.t1 }}>{selected.id}</strong> on HuggingFace.
                  {warn && <span style={{ color: C.gold }}>{' '}{warn}</span>}
                  {' '}Only a sample of rows is fetched — no data is stored on our servers.
                </span>
              </label>
            </div>

            {loadErr && (
              <div style={{ padding: '9px 13px', background: `${C.red}10`, border: `1px solid ${C.red}40`, borderRadius: 5, fontSize: 10, color: C.red, marginBottom: 10 }}>
                ⚠ {loadErr}
              </div>
            )}

            {/* Load buttons */}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {/* Sample: always fast */}
              <button
                onClick={() => loadRows(500)}
                disabled={!acknowledged}
                style={{
                  flex: '1 1 120px', padding: '11px 8px', fontFamily: FONT, fontSize: 9, fontWeight: 700,
                  background: acknowledged ? `${HF_ORANGE}15` : C.surface,
                  border:     `1px solid ${acknowledged ? HF_ORANGE : C.b1}`,
                  color:      acknowledged ? HF_ORANGE : C.t4,
                  borderRadius: 5, cursor: acknowledged ? 'pointer' : 'not-allowed',
                  transition: 'all .12s',
                }}>
                ⬇ LOAD SAMPLE
                <div style={{ fontSize: 8, marginTop: 2, opacity: 0.7, fontWeight: 400 }}>500 rows · instant</div>
              </button>
              {/* 10K rows */}
              <button
                onClick={() => loadRows(10_000)}
                disabled={!acknowledged}
                style={{
                  flex: '1 1 120px', padding: '11px 8px', fontFamily: FONT, fontSize: 9, fontWeight: 700,
                  background: acknowledged ? `${C.cyan}15` : C.surface,
                  border:     `1px solid ${acknowledged ? C.cyan : C.b1}`,
                  color:      acknowledged ? C.cyan : C.t4,
                  borderRadius: 5, cursor: acknowledged ? 'pointer' : 'not-allowed',
                  transition: 'all .12s',
                }}>
                ⬇ LOAD 10K ROWS
                <div style={{ fontSize: 8, marginTop: 2, opacity: 0.7, fontWeight: 400 }}>
                  {actualRows != null ? `${Math.min(10_000, actualRows).toLocaleString()} rows · ~10s` : '~10 sec · balanced'}
                </div>
              </button>
              {/* Load all — dynamic, shows actual size from /size API */}
              <button
                onClick={() => !tooLarge && acknowledged && loadRows(actualRows || 500_000)}
                disabled={!acknowledged || tooLarge}
                title={tooLarge ? `Dataset has ${fmtNum(actualRows)} rows — too large to load in-browser. Use LOAD 10K instead.` : undefined}
                style={{
                  flex: '1 1 140px', padding: '11px 8px', fontFamily: FONT, fontSize: 9, fontWeight: 700,
                  background: (acknowledged && !tooLarge) ? `${accent}18` : C.surface,
                  border:     `1px solid ${(acknowledged && !tooLarge) ? accent : C.b1}`,
                  color:      (acknowledged && !tooLarge) ? accent : C.t4,
                  borderRadius: 5, cursor: (acknowledged && !tooLarge) ? 'pointer' : 'not-allowed',
                  transition: 'all .12s',
                }}>
                {actualRows != null ? `⬇ LOAD ALL — ${fmtNum(actualRows)} rows` : '⬇ LOAD ALL'}
                <div style={{ fontSize: 8, marginTop: 2, opacity: 0.7, fontWeight: 400 }}>
                  {tooLarge
                    ? `Too large (${fmtNum(actualRows)}) — use 10K`
                    : actualRows != null
                      ? `${fmtBytes(sizeData?.num_bytes)} · ${actualRows > 50_000 ? '~1–2 min' : '~' + Math.ceil(actualRows / 500) + 's'}`
                      : 'loading size…'}
                </div>
              </button>
            </div>
          </>
        )}
      </div>
    );
  }

  // ── LOADING PHASE ─────────────────────────────────────────────────────────────
  return (
    <div style={{ fontFamily: FONT, textAlign: 'center', padding: '28px 0' }}>
      <div style={{ fontSize: 32, marginBottom: 14 }}>🤗</div>
      <div style={{ fontSize: 11, color: C.t1, fontWeight: 700, marginBottom: 6 }}>
        {selected?.id}
      </div>
      <div style={{ fontSize: 9, color: C.t4, marginBottom: 18, minHeight: 14 }}>{loadStage}</div>
      <div style={{
        background: C.b0, borderRadius: 4, height: 6, width: '100%',
        maxWidth: 300, margin: '0 auto 10px', overflow: 'hidden',
      }}>
        <div style={{
          background: `linear-gradient(90deg, ${HF_ORANGE}, ${accent})`,
          height: '100%', width: `${loadProgress}%`,
          transition: 'width .3s', borderRadius: 4,
        }} />
      </div>
      <div style={{ fontSize: 9, color: C.t4 }}>{loadProgress}%</div>
      {loadErr && (
        <div style={{ marginTop: 14, padding: '9px 14px', background: `${C.red}10`, border: `1px solid ${C.red}40`, borderRadius: 5, fontSize: 10, color: C.red, textAlign: 'left' }}>
          ⚠ {loadErr}
        </div>
      )}
    </div>
  );
  }; // end renderContent

  // ── Modal wrapper ──────────────────────────────────────────────────────────────────
  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.72)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div style={{ background: C.surface, border: `1px solid ${C.b1}`, borderRadius: 10, width: '100%', maxWidth: 740, maxHeight: '88vh', display: 'flex', flexDirection: 'column', boxShadow: '0 40px 120px rgba(0,0,0,.75)', overflow: 'hidden' }}>

        {/* ── Modal header ──────────────────────────────────────────────────── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderBottom: `1px solid ${C.b0}`, flexShrink: 0, background: C.deep }}>
          {phase === 'detail' && selected ? (
            <>
              <button
                onClick={() => setPhase('search')}
                style={{ padding: '4px 10px', background: C.surface, border: `1px solid ${C.b1}`, color: C.t2, fontSize: 9, fontFamily: FONT, borderRadius: 4, cursor: 'pointer', flexShrink: 0 }}>
                ← Back
              </button>
              <span style={{ fontSize: 16 }}>🤗</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 11, color: C.t1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: FONT }}>
                  {selected.id}
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 1 }}>
                  <span style={{ fontSize: 8, padding: '1px 6px', borderRadius: 10, background: `${licC}15`, color: licC, border: `1px solid ${licC}35` }}>{lic}</span>
                  {warn && <span style={{ fontSize: 8, color: C.gold }}>⚠ {warn}</span>}
                  <a href={`https://huggingface.co/datasets/${selected.id}`} target="_blank" rel="noopener noreferrer"
                    style={{ fontSize: 8.5, color: HF_ORANGE, textDecoration: 'none' }}>
                    View on HF ↗
                  </a>
                </div>
              </div>
            </>
          ) : (
            <>
              <span style={{ fontSize: 20 }}>🤗</span>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.t1, fontFamily: FONT }}>HuggingFace Datasets</div>
                <div style={{ fontSize: 9, color: C.t3, fontFamily: FONT }}>100K+ public datasets · fetched live · data stays in your browser</div>
              </div>
            </>
          )}
          <button
            onClick={() => onClose?.()}
            style={{ marginLeft: 'auto', background: 'transparent', border: '1px solid transparent', color: C.t3, fontSize: 20, cursor: 'pointer', lineHeight: 1, padding: '2px 7px', borderRadius: 4, fontFamily: FONT, flexShrink: 0, transition: 'all .12s' }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = C.b2; e.currentTarget.style.color = C.t1; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'transparent'; e.currentTarget.style.color = C.t3; }}>
            ×
          </button>
        </div>

        {/* ── Scrollable content ───────────────────────────────────────────────── */}
        <div style={{ flex: 1, overflow: 'auto', padding: 20 }}>
          {renderContent()}
        </div>

      </div>
    </div>
  );
}
